import { GoogleMap, Marker } from "@react-google-maps/api";
import React, { useState, useRef, useEffect } from "react";
import { LineDetails, Stop, Zone } from "../../types";

/* Theme */
import TransitOverlay, { type SidebarIdStrict } from "./TransitOverlay";
import { lightMapStyle } from "./MapTheme";
import { SvgMarkerIcon } from "../../assets/Icons/mapIcons/svgMarker";
import { LinePolylines } from "../layers/Polyline";

/* Utils */

import { isPureNartrafikFromStop } from "../../utils/stopType";
import { enrichStopWithCity } from "../../utils/stopCity";
import {
  getLineAliasKeys,
  getLineMapColor,
  lineMatchesId,
  normalizeLineKey,
} from "../../utils/lineIdentity";
import { getNetworkColor } from "../../utils/networkColor";

/* components */
import { Button, cn } from "@ogt/ui";
import { getStopColors } from "../../utils/mapStopColors";
/* Layers */
import ClusterManager from "../layers/ClusterManager";
import { MeasuringLayer } from "../layers/MeasuringLayer";
import LiveTrafficLayer from "../layers/LiveTrafficLayer";
import MapControllerButton from "../layers/MapControllerOptions";

import ZoneInfoWindow from "../layers/infoWindows/ZoneInfoWindow";
import CityZoneInfoWindow from "../layers/infoWindows/CityZoneInfoWindow";
import LlInfoWindow from "../layers/infoWindows/LlInfoWindow";
import ToolsPopover from "../layers/toolsPopover";
import ZonePolygon from "../layers/ZonePolygon";
import StopLocationsLayer from "../layers/StopLocationsLayer";
import CityZonePolygon from "../layers/CityZonePolygon";

/* POI layer */
import { TransitPoisLayer } from "../layers/TransitPoisLayer";

/* FitBounds */
import InitialFitBounds from "../InitialFitBounds";

/* hooks */
import { useSyncSearchParams } from "../../hooks/useSyncSearchParams";
import { useTransitLiveTraffic } from "../../hooks/useTransitLiveTraffic";
import { useMeasure } from "../../hooks/useMeasure";
import { useViewport } from "../../hooks/useViewport";
import { useSelectedLines } from "../../hooks/useSelectedLines";
import { useZones } from "../../hooks/useZones";
import { useCityZones } from "../../hooks/useCityZones";
import { useClusterIconCache } from "../../hooks/useClusterIconCache";
import { useSelectedStops } from "../../hooks/useSelectedStops";
import { useStopLocations } from "../../hooks/useStopLocations";
import { useMapInteractionGate } from "../../hooks/useMapInteractionGate";
import { useTransitStopSelection } from "../../hooks/useTransitStopSelection";
import { useViewportStopLocations } from "../../hooks/useViewportStopLocations";
import {
  useViewportFromLl,
  useClearLl,
  useMarkerText,
  useMarkerFlag,
} from "../../hooks/useViewportFromLl";
import { useStopDeeplink } from "../../hooks/useStopDeeplink";
import { useMapMarkerScale } from "../../hooks/useMapMarkerScale";
import { useActiveSelectionSearchParams } from "../../hooks/useActiveSelectionSearchParams";

/* Jotai / Atoms */
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import {
  trafficOverviewLinesAtom,
  mobileSidebarOpenAtom,
  selectedTpidAtom,
  recentItemsAtom,
  activeSelectionAtom,

  // Parking
  parkingEnabledAtom,
  parkingRequestedNameAtom,
  parkingPoisAtom,

  // TicketAgents
  ticketAgentsEnabledAtom,
  ticketAgentsRequestedNameAtom,
  ticketAgentsPoisAtom,

  // TicketMachines
  ticketMachinesEnabledAtom,
  ticketMachinesRequestedNameAtom,
  ticketMachinesPoisAtom,

  // ll
  llAtom,
} from "../../atoms";

import { useMediaQuery } from "../../hooks/useMediaQuery";
import LowZoomStopTooltip from "../layers/tooltips/LowZoomStopTooltip";

// Constants
import {
  CENTER_OF_MAP,
  INFOWINDOW_ZOOM_MIN,
  LABEL_MAX_COUNT,
  LABEL_ZOOM_MAX,
  MAP_CONTAINER_STYLE,
  MD_MEDIA_QUERY,
  STOP_LOCATIONS_HIDE_BELOW_ZOOM,
  ZOOM_THRESHOLD_LABB,
  ZOOM_THRESHOLD_LL,
  type LabelMode,
} from "./transitMap/constants";

// Helpers

import {
  clearPolylineRefById,
  clearPolylineRefs,
  rootIdOf,
  stopKeyOf,
  zoomToLine as zoomToLineHelper,
  zoomToZone,
} from "./transitMap/helpers";

/*  Types & helpers outside of component  */

type SidebarId = SidebarIdStrict | null;

/* ---------- Main Component ---------- */

export default function TransitMap() {
  const isDesktop = useMediaQuery(MD_MEDIA_QUERY);

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 5000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const tpidOverride = useAtomValue(selectedTpidAtom);

  const currentTpid = String(tpidOverride ?? "auto");
  const { expanded, getLocationsFor, toggleRoot } = useStopLocations();

  const expandedLocationsByRoot = React.useMemo(() => {
    const obj: Record<string, Stop[]> = {};
    for (const rootId of Array.from(expanded)) {
      obj[rootId] = getLocationsFor(rootId, currentTpid);
    }
    return obj;
  }, [expanded, getLocationsFor, currentTpid]);

  const setRecents = useSetAtom(recentItemsAtom);

  const markSeen = React.useCallback(
    (id: string | number) => {
      const key = stopKeyOf(id);
      setRecents((prev) => {
        const without = prev.filter(
          (x) => !(x.kind === "stop" && x.id === key)
        );
        return [
          { kind: "stop" as const, id: key, ts: Date.now() },
          ...without,
        ].slice(0, 300);
      });
    },
    [setRecents]
  );

  const [cityZoneIds, setCityZoneIds] = useState<string[]>([]);

  /* References */
  const mapRef = useRef<google.maps.Map | null>(null);
  const polyRefs = useRef<Record<string, google.maps.Polyline[]>>({});

  /* map-state */
  const { zoom, center, bounds, setZoom, setCenter, handleIdle, handleLoad } =
    useViewport(mapRef, CENTER_OF_MAP);

  // Live traffic
  const {
    showLiveByLine,
    setShowLiveByLine,
    liveLineIds,
    liveOverlayByLine,
    unavailableLineIds,
    lineStates,
    stopAllLive,
    onToggleShowLiveForLine,
  } = useTransitLiveTraffic({
    map: mapRef.current,
  });

  /* POI enabled flags */
  const parkingEnabled = useAtomValue(parkingEnabledAtom);
  const setParkingEnabled = useSetAtom(parkingEnabledAtom);
  const setParkingRequestedName = useSetAtom(parkingRequestedNameAtom);
  const setParkingPois = useSetAtom(parkingPoisAtom);

  const ticketAgentsEnabled = useAtomValue(ticketAgentsEnabledAtom);
  const setTicketAgentsEnabled = useSetAtom(ticketAgentsEnabledAtom);
  const setRequestedTicketAgentName = useSetAtom(ticketAgentsRequestedNameAtom);
  const setTicketAgentsPois = useSetAtom(ticketAgentsPoisAtom);

  const ticketMachinesEnabled = useAtomValue(ticketMachinesEnabledAtom);
  const setTicketMachinesEnabled = useSetAtom(ticketMachinesEnabledAtom);
  const setRequestedTicketMachineName = useSetAtom(
    ticketMachinesRequestedNameAtom
  );
  const setTicketMachinesPois = useSetAtom(ticketMachinesPoisAtom);

  const [mapLoaded, setMapLoaded] = useState(false);

  const [streetViewVisible, setStreetViewVisible] = useState(false);
  const [syncSearchParamsPaused, setSyncSearchParamsPaused] = useState(false);

  const streetViewListenerRef =
    React.useRef<google.maps.MapsEventListener | null>(null);

  const handleMapLoad = React.useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      handleLoad(map);
      setMapLoaded(true);

      const sv = map.getStreetView();
      setStreetViewVisible(sv.getVisible());

      streetViewListenerRef.current?.remove();
      streetViewListenerRef.current = sv.addListener("visible_changed", () => {
        setStreetViewVisible(sv.getVisible());
      });
    },
    [handleLoad]
  );

  const markerScale = useMapMarkerScale();

  const lineStopMarkerZoomScale = React.useMemo(() => {
    if (zoom < 8) return 0.25;
    if (zoom < 10) return 0.35;
    if (zoom < 12) return 0.5;
    if (zoom < 14) return 0.7;
    if (zoom < 15) return 0.85;

    return 1;
  }, [zoom]);

  const lineStopMarkerZoomScaleKey = lineStopMarkerZoomScale.toFixed(2);

  const liveMarkerZoomScale = React.useMemo(() => {
    if (zoom < 8) return 0.45;
    if (zoom < 10) return 0.45;
    if (zoom < 12) return 0.6;
    if (zoom < 14) return 0.75;
    if (zoom < 15) return 0.9;

    return 1;
  }, [zoom]);

  const liveMarkerZoomScaleKey = liveMarkerZoomScale.toFixed(2);

  React.useEffect(() => {
    return () => {
      streetViewListenerRef.current?.remove();
      streetViewListenerRef.current = null;
    };
  }, []);

  const mapOptions = React.useMemo<google.maps.MapOptions>(
    () => ({
      styles: lightMapStyle,
      disableDefaultUI: true,
      gestureHandling: "greedy",

      zoomControl: true,
      streetViewControl: true,
      mapTypeControl: false,
      keyboardShortcuts: true,

      zoomControlOptions: {
        position: isDesktop
          ? google.maps.ControlPosition.RIGHT_BOTTOM
          : google.maps.ControlPosition.LEFT_TOP,
      },

      streetViewControlOptions: {
        position: isDesktop
          ? google.maps.ControlPosition.RIGHT_BOTTOM
          : google.maps.ControlPosition.LEFT_TOP,
      },
    }),
    [isDesktop]
  );

  const [labelMode, setLabelMode] = useState<LabelMode>("icon");

  const measure = useMeasure();
  const {
    measuring,
    setMeasuring,
    measurePoints,
    setMeasurePoints,
    measureClosed,
    setMeasureClosed,
    clearMeasurement,
    totalDistanceKm,
  } = measure;

  const { zoneIds, setZoneIds, selectedZones, activeZoneId, setActiveZoneId } =
    useZones();

  const overviewLines = useAtomValue(trafficOverviewLinesAtom);

  const {
    lineIds,
    selectedLines,
    activeLineId,

    addLinesToList,
    removeLine,
    setLineIds,
  } = useSelectedLines();

  const prevOverviewRef = React.useRef<LineDetails[]>([]);

  useEffect(() => {
    if (!mapRef.current) return;

    mapRef.current.setOptions({
      draggableCursor: measuring ? "crosshair" : undefined,
    });
  }, [measuring]);

  React.useEffect(() => {
    const prev = prevOverviewRef.current;
    const next = overviewLines;

    const prevIds = new Set(prev.map((l) => String(l.id)));
    const nextIds = new Set(next.map((l) => String(l.id)));

    const selectedIds = new Set(selectedLines.map((l) => String(l.id)));

    prevIds.forEach((id) => {
      const isStillOverview = nextIds.has(id);
      const isSelected = selectedIds.has(id);

      if (!isStillOverview && !isSelected) {
        const arr = polyRefs.current[id];
        if (arr && arr.length) {
          arr.forEach((p) => p.setMap(null));
          delete polyRefs.current[id];
        }
      }
    });

    prevOverviewRef.current = next;
  }, [overviewLines, selectedLines]);

  const lineLabelById = React.useMemo(() => {
    const map: Record<string, string> = {};

    for (const line of selectedLines) {
      const id = String(line.id);
      if (!map[id]) {
        map[id] = (line as any).LineName ?? id;
      }
    }

    for (const line of overviewLines) {
      const id = String(line.id);
      if (!map[id]) {
        map[id] = (line as any).LineName ?? id;
      }
    }

    return map;
  }, [selectedLines, overviewLines]);

  const { allStops, byId, stopIds, setStopIds, activeStopId, setActiveStopId } =
    useSelectedStops();

  const listedStopRootIds = React.useMemo(
    () => new Set(stopIds.map(rootIdOf)),
    [stopIds]
  );

  const {
    locationsByRoot: viewportLocationsByRoot,
    displayedRoots: displayedLocationRoots,
  } = useViewportStopLocations({
    stops: allStops,
    bounds,
    zoom,
    tpid: currentTpid,
  });
  const locationsByRoot = React.useMemo(
    () => ({
      ...expandedLocationsByRoot,
      ...viewportLocationsByRoot,
    }),
    [expandedLocationsByRoot, viewportLocationsByRoot]
  );

  const [fallbackStopsById, setFallbackStopsById] = React.useState<
    Map<string, Stop>
  >(new Map());

  const selectedStopsForUi = React.useMemo(() => {
    return stopIds
      .map((id) => {
        const key = String(id);
        const root = rootIdOf(key);

        const base =
          byId.get(key) ??
          byId.get(root) ??
          allStops.find((s) => rootIdOf(String(s.id)) === root);

        const fallback =
          fallbackStopsById.get(key) ??
          fallbackStopsById.get(root) ??
          Array.from(fallbackStopsById.values()).find(
            (s) => rootIdOf(String(s.id)) === root
          );

        if (!base && !fallback) return null;

        return {
          ...(base ?? fallback),
          ...(fallback ?? {}),
        } as Stop;
      })
      .filter(Boolean) as Stop[];
  }, [stopIds, byId, allStops, fallbackStopsById]);

  //  ADAPTER FUNCTIONS

  const visibleStopsOnMap = allStops;

  const {
    selectedCityZones,
    activeCityZoneId,
    setActiveCityZoneId,
    addAndActivateCityZone,
    removeCityZone,
  } = useCityZones({ cityZoneIds, onChangeIds: setCityZoneIds });

  useSyncSearchParams(
    "linje",
    lineIds,
    setLineIds,
    undefined,
    syncSearchParamsPaused
  );

  useSyncSearchParams(
    "hallplats",
    stopIds,
    setStopIds,
    undefined,
    syncSearchParamsPaused
  );

  useSyncSearchParams(
    "nartrafik",
    zoneIds,
    setZoneIds,
    undefined,
    syncSearchParamsPaused
  );

  useSyncSearchParams(
    "stadszon",
    cityZoneIds,
    setCityZoneIds,
    undefined,
    syncSearchParamsPaused
  );

  // ll
  const setActiveSelection = useSetAtom(activeSelectionAtom);
  const activeSelection = useAtomValue(activeSelectionAtom);
  const clearLl = useClearLl();

  const {
    searchParams,
    setSearchParams,
    activeStopParam,
    activeLineParam,

    setActiveStopParam: setActiveStopParamAdapter,
    setActiveLineParam: setActiveLineParamAdapter,
    clearActiveParams,
  } = useActiveSelectionSearchParams({
    activeSelection,
    setActiveSelection,
    paused: syncSearchParamsPaused,
  });

  const liveActiveLineId =
    activeSelection?.kind === "line" ? String(activeSelection.id) : null;

  const previousLiveActiveLineIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const previousId = previousLiveActiveLineIdRef.current;

    if (previousId === liveActiveLineId) return;

    if (liveActiveLineId !== null && (!mapLoaded || !mapRef.current)) {
      return;
    }

    previousLiveActiveLineIdRef.current = liveActiveLineId;

    stopAllLive();

    setShowLiveByLine(
      liveActiveLineId !== null
        ? new Set([liveActiveLineId])
        : new Set<string>()
    );
  }, [liveActiveLineId, mapLoaded, stopAllLive, setShowLiveByLine]);

  const pendingClearRef = React.useRef<{
    kind: "stops" | "all";
    targetSearch: string;
  } | null>(null);

  React.useEffect(() => {
    const pending = pendingClearRef.current;

    if (!pending || !syncSearchParamsPaused) return;

    const urlReady = searchParams.toString() === pending.targetSearch;

    const stopsReady = stopIds.length === 0 && activeSelection?.kind !== "stop";

    const stateReady =
      pending.kind === "stops"
        ? stopsReady
        : stopsReady &&
          lineIds.length === 0 &&
          zoneIds.length === 0 &&
          cityZoneIds.length === 0 &&
          activeSelection === null;

    if (!urlReady || !stateReady) return;

    pendingClearRef.current = null;
    setSyncSearchParamsPaused(false);
  }, [
    searchParams,
    syncSearchParamsPaused,
    stopIds,
    lineIds,
    zoneIds,
    cityZoneIds,
    activeSelection,
  ]);

  // STOP deeplink
  const { stopMissing } = useStopDeeplink({
    stopIds,
    byId,
    allStops,
  });

  const markerTextFromUrl = useMarkerText();
  const ll = useAtomValue(llAtom);
  const markerFromUrl = useMarkerFlag();
  const [llInfoOpen, setLlInfoOpen] = React.useState(true);
  const stopLookupReady = byId.size > 0 || allStops.length > 0;
  const hasSingleStopParam = stopIds.length === 1;

  const hasStopMatch = hasSingleStopParam && stopLookupReady && !stopMissing;

  // fallback only when stop ID is in URL
  const isFallback = Boolean(
    hasSingleStopParam && stopLookupReady && stopMissing && ll
  );

  // Prevent markerText/ll from showing if stop is found
  const showMarker = Boolean(
    ll && !hasStopMatch && (markerFromUrl || markerTextFromUrl || isFallback)
  );

  const showPopup = Boolean(
    ll && !hasStopMatch && (markerTextFromUrl || isFallback)
  );

  const llLabel =
    !hasStopMatch && markerTextFromUrl?.trim()
      ? markerTextFromUrl.trim()
      : isFallback
      ? "Ingen träff på plats"
      : undefined;

  React.useEffect(() => {
    if (!mapLoaded) return;

    if (!hasStopMatch && (markerTextFromUrl || isFallback)) {
      setLlInfoOpen(true);
    }
  }, [mapLoaded, markerTextFromUrl, isFallback, hasStopMatch]);

  React.useEffect(() => {
    if (!mapLoaded) return;

    if (hasStopMatch) {
      setLlInfoOpen(false);
    }
  }, [mapLoaded, hasStopMatch]);

  React.useEffect(() => {
    if (!mapLoaded) return;
    if (!hasStopMatch) return;

    const params = new URLSearchParams(searchParams);

    const hasFallbackParams =
      params.has("ll") || params.has("markerText") || params.has("marker");

    if (!hasFallbackParams) return;

    params.delete("ll");
    params.delete("markerText");
    params.delete("marker");

    setSearchParams(params, { replace: true });
    clearLl(null);
  }, [mapLoaded, hasStopMatch, searchParams, setSearchParams, clearLl]);

  const hasAnyFilter =
    selectedLines.length > 0 ||
    selectedStopsForUi.length > 0 ||
    selectedZones.length > 0 ||
    selectedCityZones.length > 0 ||
    parkingEnabled ||
    ticketAgentsEnabled ||
    ticketMachinesEnabled ||
    overviewLines.length > 0 ||
    showLiveByLine.size > 0 ||
    !!ll;

  const handleZoomToZone = React.useCallback(
    (zone: Zone) => {
      const map = mapRef.current;
      if (!map) return;

      const lineId = String(zone.id).split("_")[0];
      const bounds = new google.maps.LatLngBounds();

      const extendBounds = (lat: number, lng: number) => {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        bounds.extend({ lat, lng });
      };

      for (const point of zone.polygon) {
        extendBounds(point.lat, point.lng);
      }

      for (const stop of allStops) {
        if (!isPureNartrafikFromStop(stop)) continue;

        const matchesLine = (stop.servedLineIds ?? []).some(
          (id) => String(id).trim() === lineId
        );

        if (!matchesLine) continue;

        extendBounds(stop.lat, stop.lng);
      }

      if (bounds.isEmpty()) return;

      map.fitBounds(bounds, {
        top: 60,
        right: 60,
        bottom: 100,
        left: 60,
      });
    },
    [allStops]
  );

  const lastZoomedZoneIdRef = React.useRef<string | null>(null);

  const stopColors = React.useMemo(getStopColors, []);

  useEffect(() => {
    if (!mapLoaded) return;
    if (!activeZoneId) return;
    if (!mapRef.current) return;

    if (lastZoomedZoneIdRef.current === activeZoneId) return;

    const z = selectedZones.find((zz) => zz.id === activeZoneId);
    if (!z) return;

    handleZoomToZone(z);
    lastZoomedZoneIdRef.current = activeZoneId;
  }, [mapLoaded, activeZoneId, selectedZones, handleZoomToZone]);

  const handleZoneClick = React.useCallback(
    (z: Zone, e: google.maps.MapMouseEvent) => {
      if (z.id !== activeZoneId) setActiveZoneId(z.id);

      const pos = e.latLng
        ? { lat: e.latLng.lat(), lng: e.latLng.lng() }
        : z.polygon[0];

      setZoneInfo({ zone: z, position: pos });
    },
    [activeZoneId, setActiveZoneId]
  );

  // Add Stop from Line
  const handleAddStopFromLine = React.useCallback(
    (stop: Stop) => {
      const normalizedStop: Stop = {
        ...stop,
        id: String(stop.id),
      };
      setFallbackStopsById((previous) => {
        const next = new Map(previous);
        next.set(normalizedStop.id, normalizedStop);
        return next;
      });

      setStopIds((current) =>
        current.includes(normalizedStop.id)
          ? current
          : [normalizedStop.id, ...current]
      );
    },
    [setStopIds]
  );

  const setTrafficOverviewLines = useSetAtom(trafficOverviewLinesAtom);

  // Clear all Filters
  const handleClearAll = () => {
    setSyncSearchParamsPaused(true);

    pendingClearRef.current = {
      kind: "all",
      targetSearch: "",
    };

    setSyncSearchParamsPaused(true);

    setLineIds([]);
    setStopIds([]);
    setZoneIds([]);
    setCityZoneIds([]);
    setTrafficOverviewLines([]);

    setActiveSelection(null);
    setActiveStopId(null);

    clearLl(null);

    expanded.forEach((rootId) => {
      void toggleRoot(rootId, false);
    });

    setShowLiveByLine(new Set());
    stopAllLive();

    clearPolylineRefs(polyRefs);

    setActiveZoneId(null);
    setActiveCityZoneId(null);

    setZoneInfo(null);
    setCityZoneInfo(null);

    setParkingEnabled(false);
    setParkingRequestedName(null);
    setParkingPois([]);

    setTicketAgentsEnabled(false);
    setRequestedTicketAgentName(null);
    setTicketAgentsPois([]);

    setTicketMachinesEnabled(false);
    setRequestedTicketMachineName(null);
    setTicketMachinesPois([]);

    setFallbackStopsById(new Map());

    setSearchParams(new URLSearchParams(), {
      replace: true,
    });
  };

  const handleClearActive = React.useCallback(() => {
    const activeStopRootId = activeStopId ? rootIdOf(activeStopId) : null;

    setShowLiveByLine(new Set());
    stopAllLive();

    setActiveSelection(null);

    if (activeStopRootId && expanded.has(activeStopRootId)) {
      void toggleRoot(activeStopRootId, false);
    }

    clearActiveParams();
  }, [
    activeStopId,
    expanded,
    toggleRoot,
    setActiveSelection,
    clearActiveParams,
    setShowLiveByLine,
    stopAllLive,
  ]);

  const [hasSelectedLineNetwork, setHasSelectedLineNetwork] =
    React.useState(false);

  const handleAddLineNetwork = React.useCallback(
    (lines: LineDetails[]) => {
      if (!lines.length) return;

      setHasSelectedLineNetwork(true);
      handleClearActive();
      addLinesToList(lines);

      const map = mapRef.current;
      if (!map) return;

      const bounds = new google.maps.LatLngBounds();

      const extendBounds = (point: google.maps.LatLngLiteral) => {
        if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
          return;
        }

        bounds.extend(point);
      };

      for (const line of lines) {
        let hasPathPoints = false;

        for (const path of line.paths ?? []) {
          for (const point of path) {
            if (Number.isFinite(point.lat) && Number.isFinite(point.lng)) {
              extendBounds(point);
              hasPathPoints = true;
            }
          }
        }

        if (!hasPathPoints) {
          for (const stop of line.stops ?? []) {
            extendBounds({ lat: stop.lat, lng: stop.lng });
          }
        }
      }

      if (bounds.isEmpty()) return;

      map.fitBounds(bounds, {
        top: 60,
        right: 60,
        bottom: 80,
        left: isDesktop ? 160 : 60,
      });
    },
    [addLinesToList, handleClearActive, isDesktop]
  );

  const handleClearStopsOnly = React.useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);

    nextParams.delete("hallplats");
    nextParams.delete("aktivHallplats");

    pendingClearRef.current = {
      kind: "stops",
      targetSearch: nextParams.toString(),
    };

    setSyncSearchParamsPaused(true);

    setStopIds([]);
    setFallbackStopsById(new Map());

    setActiveSelection((current) =>
      current?.kind === "stop" ? null : current
    );

    expanded.forEach((rootId) => {
      void toggleRoot(rootId, false);
    });

    setSearchParams(nextParams, { replace: true });
  }, [
    searchParams,
    setStopIds,
    setFallbackStopsById,
    setActiveSelection,
    expanded,
    toggleRoot,
    setSearchParams,
  ]);
  const zoomToLine = React.useCallback((line: LineDetails) => {
    zoomToLineHelper(mapRef.current, line);
  }, []);

  const handleRemoveLine = React.useCallback(
    (idRaw: string) => {
      const id = String(idRaw);

      const removedLine = selectedLines.find((line) => {
        const canonical = String(line.id);
        const alt = String((line as any).LineNrReal ?? "");
        return canonical === id || alt === id;
      });

      const removedIds = new Set<string>([id]);
      if (removedLine) {
        removedIds.add(String(removedLine.id));
        const alt = (removedLine as any).LineNrReal;
        if (alt != null && String(alt).trim() !== "") {
          removedIds.add(String(alt));
        }
      }

      const activeSelectionLineId =
        activeSelection?.kind === "line" ? String(activeSelection.id) : null;

      removeLine(id);

      if (activeLineParam != null && removedIds.has(String(activeLineParam))) {
        setActiveLineParamAdapter(null);
      }

      if (
        (activeLineParam != null && removedIds.has(String(activeLineParam))) ||
        (activeSelectionLineId != null && removedIds.has(activeSelectionLineId))
      ) {
        setActiveSelection(null);
      }

      setShowLiveByLine((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      clearPolylineRefById(polyRefs, id);
    },
    [
      activeSelection,
      activeLineParam,
      selectedLines,
      setActiveSelection,
      setActiveLineParamAdapter,
      setShowLiveByLine,
      removeLine,
    ]
  );

  // cityzone
  const zoomToCityZone = React.useCallback((zone: Zone) => {
    zoomToZone(mapRef.current, zone);
  }, []);

  const handleAddAndActivateCityZone = (zone: Zone) => {
    addAndActivateCityZone(zone);
    zoomToCityZone(zone);
  };

  const handleRemoveCityZone = (id: string) => {
    removeCityZone(id);
  };

  const handleSetActiveCityZoneId = (id: string | null) => {
    if (id === activeCityZoneId) return;
    setActiveCityZoneId(id);
    const z = id ? selectedCityZones.find((zz) => zz.id === id) : null;
    if (mapRef.current && z) {
      mapRef.current.panTo(z.polygon[0]);
      mapRef.current.setZoom(12);
    }
  };

  const getCachedClusterIcon = useClusterIconCache();
  const getClusterIconCb = React.useCallback(
    (count: number, dimmed: boolean, hovered?: boolean) =>
      getCachedClusterIcon(count, dimmed, hovered),
    [getCachedClusterIcon]
  );

  const baseClusterRadius =
    zoom < 10 ? 400 : zoom < 12 ? 340 : zoom < 14 ? 250 : 180;

  const scaledClusterRadius = Math.max(
    60,
    Math.round(baseClusterRadius * markerScale)
  );

  const [openSidebarId, setOpenSidebarId] = useState<SidebarId>(null);
  const [zoneInfo, setZoneInfo] = useState<{
    zone: Zone;
    position: google.maps.LatLngLiteral;
  } | null>(null);

  const [mobileSidebarOpen, setMobileSidebarOpen] = useAtom(
    mobileSidebarOpenAtom
  );

  useEffect(() => {
    if (!streetViewVisible) return;

    setOpenSidebarId(null);
    setMobileSidebarOpen(false);
    setOpen(false);
  }, [streetViewVisible, setMobileSidebarOpen]);

  const handleSidebarToggle = React.useCallback(
    (id: SidebarIdStrict) => {
      setOpenSidebarId((cur) => {
        const next = cur === id ? null : (id as SidebarId);
        setMobileSidebarOpen(Boolean(next));
        return next;
      });
    },
    [setMobileSidebarOpen]
  );

  const {
    suppressActiveStopUrlSyncOnce,
    selectStopGroupFromId,
    handleStopMarkerClick,
    handleRemoveStop,
    handleSetActiveStopId,
  } = useTransitStopSelection({
    mapRef,
    allStops,

    stopIds,
    setStopIds,

    activeStopId,
    activeStopParam,

    expanded,
    openSidebarId,

    setActiveStopId,
    setActiveStopParam: setActiveStopParamAdapter,
    setActiveSelection,

    toggleRoot,
    toggleSidebar: handleSidebarToggle,

    markSeen,
    stopAllLive,
    setShowLiveByLine,
  });

  const handleAddAndActivateLine = React.useCallback(
    (line: string | LineDetails) => {
      const id = typeof line === "string" ? String(line) : String(line.id);

      suppressActiveStopUrlSyncOnce();
      setActiveStopId(null);
      // Add line to URL and atom
      setLineIds((cur) => (cur.includes(id) ? cur : [id, ...cur]));
      // Update URL params
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (!params.getAll("linje").includes(id)) {
            params.append("linje", id);
          }
          params.delete("aktivHallplats");
          params.set("aktivLinje", id);
          return params;
        },
        { replace: true }
      );

      // Update active selection to line
      setActiveSelection({ kind: "line", id });

      if (activeStopId) {
        const rootId = rootIdOf(activeStopId);
        if (expanded.has(rootId)) {
          void toggleRoot(rootId, false);
        }
      }

      if (typeof line !== "string") {
        zoomToLine(line);
      }

      setOpenSidebarId("stopAndLines");
      setMobileSidebarOpen(true);
    },
    [
      activeStopId,
      expanded,
      toggleRoot,
      suppressActiveStopUrlSyncOnce,
      setActiveStopId,
      setSearchParams,
      setActiveSelection,
      setShowLiveByLine,
      zoomToLine,
      setMobileSidebarOpen,
      setLineIds,
    ]
  );

  // Add line by ID (used for deeplinking from stops)
  const addLineById = React.useCallback(
    (lineId: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (!params.getAll("linje").includes(lineId)) {
            params.append("linje", lineId);
          }
          return params;
        },
        { replace: true }
      );
      setOpenSidebarId("stopAndLines");
      setMobileSidebarOpen(true);
    },
    [setSearchParams, setMobileSidebarOpen]
  );

  const [visibleSingletonStopIds, setVisibleSingletonStopIds] = useState<
    Set<string>
  >(new Set());

  const mapSettled = useMapInteractionGate(mapRef, mapLoaded, 150);

  // Only show singletons when in icon+name mode, map is settled and zoomed in enough
  const shouldTrackSingletons =
    labelMode === "icon+name" && mapSettled && zoom <= LABEL_ZOOM_MAX;

  const lowZoomInfoStops = React.useMemo(() => {
    if (labelMode !== "icon+name") return [];
    if (!mapSettled) return [];
    if (zoom > LABEL_ZOOM_MAX) return [];
    if (visibleSingletonStopIds.size === 0) return [];

    const bounds = mapRef.current?.getBounds();
    if (!bounds) return [];

    const res: Stop[] = [];

    for (const s of visibleStopsOnMap) {
      const sid = String(s.id);
      const root = sid.split("-")[0];

      if (
        !visibleSingletonStopIds.has(sid) &&
        !visibleSingletonStopIds.has(root)
      ) {
        continue;
      }

      if (!bounds.contains(new google.maps.LatLng(s.lat, s.lng))) continue;

      res.push(s);
      if (res.length >= LABEL_MAX_COUNT) break;
    }

    return res;
  }, [labelMode, mapSettled, zoom, visibleStopsOnMap, visibleSingletonStopIds]);

  const activeLineIdStr = React.useMemo(() => {
    if (activeSelection?.kind === "line") return String(activeSelection.id);
    if (activeLineParam) return String(activeLineParam);
    if (activeLineId) return String(activeLineId);
    return null;
  }, [activeSelection, activeLineParam, activeLineId]);

  const activeLine = React.useMemo(() => {
    if (!activeLineIdStr) return null;

    return (
      selectedLines.find((l) => lineMatchesId(l, activeLineIdStr)) ??
      overviewLines.find((l) => lineMatchesId(l, activeLineIdStr)) ??
      null
    );
  }, [activeLineIdStr, selectedLines, overviewLines]);

  const activeLineColor = React.useMemo(() => {
    return activeLine ? getLineMapColor(activeLine) : null;
  }, [activeLine]);

  const selectedLineStopRootIds = React.useMemo(() => {
    if (selectedLines.length === 0) return null;

    const roots = new Set<string>();

    for (const line of selectedLines) {
      for (const stop of line.stops ?? []) {
        roots.add(rootIdOf(String(stop.id)));
      }
    }

    for (const stop of visibleStopsOnMap) {
      const servedLineIds = (stop as any).servedLineIds ?? [];

      if (
        servedLineIds.some((id: unknown) =>
          selectedLines.some((line) => lineMatchesId(line, id))
        )
      ) {
        roots.add(rootIdOf(String(stop.id)));
      }
    }

    return roots.size > 0 ? roots : null;
  }, [selectedLines, visibleStopsOnMap]);

  const selectedNartrafikLineIds = React.useMemo(
    () => new Set(zoneIds.map((id) => String(id).split("_")[0])),
    [zoneIds]
  );

  const zoneStopRootIds = React.useMemo(() => {
    const roots = new Set<string>();

    for (const stop of allStops) {
      const matchesZone = (stop.servedLineIds ?? []).some((lineId) =>
        selectedNartrafikLineIds.has(String(lineId))
      );

      if (isPureNartrafikFromStop(stop) && matchesZone) {
        roots.add(rootIdOf(stop.id));
      }
    }

    return roots;
  }, [allStops, selectedNartrafikLineIds]);

  const activeZoneLineId = React.useMemo(() => {
    if (!activeZoneId || !zoneIds.includes(activeZoneId)) return null;

    return String(activeZoneId).split("_")[0].trim();
  }, [activeZoneId, zoneIds]);

  const activeZoneColor = React.useMemo(
    () => (activeZoneLineId ? getNetworkColor(activeZoneLineId) : null),
    [activeZoneLineId]
  );

  const activeZoneStopRootIds = React.useMemo(() => {
    const roots = new Set<string>();

    if (!activeZoneLineId) return roots;

    for (const stop of allStops) {
      const matchesZone = (stop.servedLineIds ?? []).some(
        (lineId) => String(lineId).trim() === activeZoneLineId
      );

      if (isPureNartrafikFromStop(stop) && matchesZone) {
        roots.add(rootIdOf(stop.id));
      }
    }

    return roots;
  }, [allStops, activeZoneLineId]);

  const activeZoneMarkerKey = React.useMemo(
    () =>
      JSON.stringify([
        activeZoneLineId,
        activeZoneColor,
        Array.from(activeZoneStopRootIds).sort(),
      ]),
    [activeZoneLineId, activeZoneColor, activeZoneStopRootIds]
  );

  const focusedStopRootIds = React.useMemo(() => {
    const roots = new Set(selectedLineStopRootIds ?? []);

    for (const rootId of listedStopRootIds) {
      roots.add(rootId);
    }

    for (const rootId of zoneStopRootIds) {
      roots.add(rootId);
    }

    if (activeStopId) {
      roots.add(rootIdOf(activeStopId));
    }

    const hasFocus =
      Boolean(selectedLineStopRootIds) ||
      Boolean(activeStopId) ||
      selectedNartrafikLineIds.size > 0;

    return hasFocus ? roots : null;
  }, [
    selectedLineStopRootIds,
    listedStopRootIds,
    zoneStopRootIds,
    activeStopId,
    selectedNartrafikLineIds,
  ]);

  const forceVisibleStopRootIds = React.useMemo(() => {
    const roots = new Set(selectedLineStopRootIds ?? []);

    for (const rootId of zoneStopRootIds) {
      roots.add(rootId);
    }

    return roots.size > 0 ? roots : null;
  }, [selectedLineStopRootIds, zoneStopRootIds]);

  const selectedLineStopsForMap = React.useMemo(() => {
    if (!selectedLineStopRootIds) return [];

    const result: Stop[] = [];
    const seen = new Set<string>();

    for (const stop of visibleStopsOnMap) {
      const rootId = rootIdOf(String(stop.id));

      if (!selectedLineStopRootIds.has(rootId)) continue;
      if (seen.has(String(stop.id))) continue;

      seen.add(String(stop.id));
      result.push(stop);
    }

    for (const line of selectedLines) {
      for (const stop of line.stops ?? []) {
        if (seen.has(String(stop.id))) continue;

        seen.add(String(stop.id));
        result.push(stop);
      }
    }

    return result;
  }, [selectedLineStopRootIds, visibleStopsOnMap, selectedLines]);

  const clusterStopsOnMap = React.useMemo(() => {
    const byId = new Map<string, Stop>();

    for (const stop of visibleStopsOnMap) {
      byId.set(String(stop.id), stop);
    }

    for (const stop of selectedLineStopsForMap) {
      byId.set(String(stop.id), stop);
    }

    return Array.from(byId.values());
  }, [visibleStopsOnMap, selectedLineStopsForMap]);

  const clusterFocusKey = React.useMemo(() => {
    return Array.from(selectedLineStopRootIds ?? [])
      .sort()
      .join("|");
  }, [selectedLineStopRootIds]);

  const markerScaleKey = markerScale.toFixed(2);

  const hasLineStopFocus = Boolean(selectedLineStopRootIds);

  const lineStopScalePart = hasLineStopFocus
    ? `-lineStopScale:${lineStopMarkerZoomScaleKey}`
    : "";

  const clusterKey = `clusters-tpid:${
    tpidOverride ?? "auto"
  }-lines:${lineIds.join(
    ","
  )}-focus:${clusterFocusKey}-scale:${markerScaleKey}${lineStopScalePart}`;

  const selectedLineColorByStopRootId = React.useMemo(() => {
    const map = new Map<string, string>();
    const colorByLineId = new Map<string, string>();

    const setLineAliases = (line: LineDetails, color: string) => {
      for (const alias of getLineAliasKeys(line)) {
        colorByLineId.set(alias, color);
      }
    };

    const setLineStops = (
      line: LineDetails,
      color: string,
      overwrite: boolean
    ) => {
      for (const stop of line.stops ?? []) {
        const rootId = rootIdOf(String(stop.id));

        if (overwrite || !map.has(rootId)) {
          map.set(rootId, color);
        }
      }
    };

    for (const line of selectedLines) {
      setLineAliases(line, getLineMapColor(line));
    }

    for (const line of selectedLines) {
      if (lineMatchesId(line, activeLineIdStr)) continue;

      setLineStops(line, getLineMapColor(line), false);
    }

    for (const stop of visibleStopsOnMap) {
      const rootId = rootIdOf(String(stop.id));
      if (map.has(rootId)) continue;

      const servedLineIds = (stop as any).servedLineIds ?? [];

      for (const servedId of servedLineIds) {
        const color = colorByLineId.get(normalizeLineKey(servedId));

        if (color) {
          map.set(rootId, color);
          break;
        }
      }
    }

    const activeSelectedLine = selectedLines.find((line) =>
      lineMatchesId(line, activeLineIdStr)
    );

    if (activeSelectedLine) {
      const activeColor = getLineMapColor(activeSelectedLine);
      const activeAliases = new Set(getLineAliasKeys(activeSelectedLine));

      setLineStops(activeSelectedLine, activeColor, true);

      for (const stop of visibleStopsOnMap) {
        const servedLineIds = (stop as any).servedLineIds ?? [];

        if (
          servedLineIds.some((id: unknown) =>
            activeAliases.has(normalizeLineKey(id))
          )
        ) {
          map.set(rootIdOf(String(stop.id)), activeColor);
        }
      }
    }

    return map;
  }, [selectedLines, activeLineIdStr, visibleStopsOnMap]);

  const activeLineStopRootIds = React.useMemo(() => {
    if (!activeLine || !activeLineIdStr) return null;

    const roots = new Set<string>();

    for (const stop of activeLine.stops ?? []) {
      roots.add(rootIdOf(String(stop.id)));
    }

    const activeIds = new Set(getLineAliasKeys(activeLine));

    for (const stop of visibleStopsOnMap) {
      const servedLineIds = (stop as any).servedLineIds ?? [];

      if (
        servedLineIds.some((id: unknown) => activeIds.has(normalizeLineKey(id)))
      ) {
        roots.add(rootIdOf(String(stop.id)));
      }
    }

    return roots.size > 0 ? roots : null;
  }, [activeLine, activeLineIdStr, visibleStopsOnMap]);

  const getStopIconCb = React.useCallback(
    (
      s: Stop,
      state?: {
        hovered?: boolean;
        focused?: boolean;
        focusedColor?: string;
      }
    ) => {
      const pure = isPureNartrafikFromStop(s);
      const rootId = rootIdOf(s.id);

      const zoneColor =
        pure && activeZoneStopRootIds.has(rootId) ? activeZoneColor : null;

      const isActive =
        activeStopId != null && rootId === rootIdOf(activeStopId);

      const isListed = listedStopRootIds.has(rootId);
      const isHovered = Boolean(state?.hovered);

      const selectedLineColor = selectedLineColorByStopRootId.get(rootId);
      const isActiveLineStop = activeLineStopRootIds?.has(rootId) ?? false;
      const isSelectedLineStop = Boolean(selectedLineColor);

      const lineColor =
        isActiveLineStop && activeLineColor
          ? activeLineColor
          : selectedLineColor ?? state?.focusedColor;

      const baseMarkerHeight = isActive || isHovered ? 48 : 40;
      const markerHeight = baseMarkerHeight * markerScale;

      let fillColor = isHovered ? stopColors.ghostBgHover : stopColors.ghostBg;

      let strokeColor = stopColors.ghostBorder;
      let iconColor = stopColors.ghostIcon;

      if (isActive) {
        fillColor = stopColors.activeBg;
        strokeColor = stopColors.activeBg;
        iconColor = stopColors.activeIcon;
      } else if (zoneColor) {
        fillColor = zoneColor;
        strokeColor = stopColors.activeIcon;
        iconColor = stopColors.activeIcon;
      } else if (isListed) {
        fillColor = stopColors.selectedBg;
        strokeColor = stopColors.selectedBg;
        iconColor = stopColors.selectedIcon;
      } else if (pure) {
        fillColor = "#0078C7";
        strokeColor = "#0078C7";
        iconColor = stopColors.activeIcon;
      } else if (isActiveLineStop && lineColor) {
        fillColor = lineColor;
        strokeColor = stopColors.activeIcon;
        iconColor = stopColors.activeIcon;
      } else if (isSelectedLineStop && lineColor) {
        strokeColor = lineColor;
      }

      const glass =
        state?.focused === false && !isActive && !isListed && !zoneColor;

      return SvgMarkerIcon({
        topIconName: pure ? "n" : "h",
        badgeIconName: undefined,
        height: Math.round(markerHeight),

        fillColor: glass ? stopColors.ghostBg : fillColor,
        strokeColor: glass ? stopColors.ghostBorder : strokeColor,
        iconColor: glass ? stopColors.ghostIcon : iconColor,

        glass,
        glassHovered: glass && isHovered,
      });
    },
    [
      activeStopId,
      listedStopRootIds,
      stopColors,
      selectedLineColorByStopRootId,
      activeLineStopRootIds,
      activeLineColor,
      activeZoneColor,
      activeZoneStopRootIds,
      markerScale,
    ]
  );
  const handleStopLocationClick = React.useCallback(
    (stop: Stop) => {
      const id = String(stop.id);
      const rootId = rootIdOf(id);

      setShowLiveByLine(new Set());
      stopAllLive();

      const parentStop =
        byId.get(rootId) ??
        allStops.find((item) => String(item.id) === rootId) ??
        allStops.find((item) => rootIdOf(item.id) === rootId);

      const stopForList: Stop = {
        ...(parentStop ?? stop),
        id: rootId,
        name: parentStop?.name ?? stop.name.replace(/\s*\([^)]*\)\s*$/, ""),
      };

      setFallbackStopsById((previous) => {
        const next = new Map(previous);
        next.set(rootId, stopForList);
        return next;
      });

      setStopIds((previous) => {
        const alreadyListed = previous.some(
          (stopId) => rootIdOf(stopId) === rootId
        );

        return alreadyListed ? previous : [rootId, ...previous];
      });

      handleSetActiveStopId(id);

      markSeen(rootId);

      if (openSidebarId !== "stopAndLines") {
        handleSidebarToggle("stopAndLines");
      }
    },
    [
      byId,
      allStops,
      setStopIds,
      setShowLiveByLine,
      stopAllLive,
      handleSetActiveStopId,
      markSeen,
      openSidebarId,
      handleSidebarToggle,
    ]
  );
  const handleSelectStopFromSearch = React.useCallback(
    (stop: Stop) => {
      const normalizedStop: Stop = {
        ...stop,
        id: String(stop.id),
      };

      setFallbackStopsById((previous) => {
        const next = new Map(previous);
        next.set(normalizedStop.id, normalizedStop);
        return next;
      });

      selectStopGroupFromId(normalizedStop.id, {
        lat: normalizedStop.lat,
        lng: normalizedStop.lng,
      });

      void enrichStopWithCity(normalizedStop)
        .then((enrichedStop) => {
          const normalizedEnrichedStop: Stop = {
            ...enrichedStop,
            id: String(enrichedStop.id),
          };

          setFallbackStopsById((previous) => {
            const next = new Map(previous);
            next.set(normalizedEnrichedStop.id, normalizedEnrichedStop);
            return next;
          });
        })
        .catch((error) => {
          console.error("Kunde inte berika hållplatsen", error);
        });
    },
    [selectStopGroupFromId]
  );
  const activeStopLocation = React.useMemo(() => {
    if (!activeStopId) return null;

    const rootId = rootIdOf(activeStopId);

    return (
      locationsByRoot[rootId]?.find(
        (s) => String(s.id) === String(activeStopId)
      ) ?? null
    );
  }, [activeStopId, locationsByRoot]);

  const handleCopyLink = React.useCallback(() => {
    const url = window.location.href;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => setCopied(true));
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
      } finally {
        document.body.removeChild(textArea);
      }
    }
  }, []);

  const handleMapClick = React.useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!measuring) {
        handleClearActive();
        return;
      }

      const ll = e.latLng;
      if (!ll) return;

      const next = { lat: ll.lat(), lng: ll.lng() };
      if (measureClosed) return;

      if (measurePoints.length >= 2) {
        const first = measurePoints[0];
        const d = google.maps.geometry.spherical.computeDistanceBetween(
          new google.maps.LatLng(first.lat, first.lng),
          new google.maps.LatLng(next.lat, next.lng)
        );
        const SNAP_METERS = 20;
        if (d <= SNAP_METERS) {
          setMeasurePoints((prev) => [...prev, first]);
          setMeasureClosed(true);
          return;
        }
      }

      setMeasurePoints((prev) => [...prev, next]);
    },
    [
      measuring,
      measureClosed,
      measurePoints,
      setMeasurePoints,
      setMeasureClosed,
      handleClearActive,
    ]
  );

  const [cityZoneInfo, setCityZoneInfo] = useState<{
    zone: Zone;
    position: google.maps.LatLngLiteral;
  } | null>(null);

  const handleCityZoneClick = React.useCallback(
    (z: Zone, e: google.maps.MapMouseEvent) => {
      if (z.id !== activeCityZoneId) setActiveCityZoneId(z.id);

      const pos = e.latLng
        ? { lat: e.latLng.lat(), lng: e.latLng.lng() }
        : z.polygon[0];

      setCityZoneInfo({ zone: z, position: pos });
      zoomToCityZone(z);
    },
    [activeCityZoneId, setActiveCityZoneId, zoomToCityZone]
  );
  const clusterStopsForMap = React.useMemo(() => {
    if (displayedLocationRoots.size === 0) {
      return clusterStopsOnMap;
    }

    return clusterStopsOnMap.filter(
      (stop) => !displayedLocationRoots.has(rootIdOf(stop.id))
    );
  }, [clusterStopsOnMap, displayedLocationRoots]);

  useViewportFromLl({
    mapLoaded,
    mapRef,
    zoomTo: ZOOM_THRESHOLD_LL,

    // stop-deeplink
    primaryId: stopIds.length === 1 ? stopIds[0] : null,
    primaryLookupReady: byId.size > 0 || allStops.length > 0,
    hasPrimaryMatch: stopIds.length === 1 ? !stopMissing : false,

    llKey: "ll",
    setCenter,
    setZoom,
  });

  const activeStopLocations = React.useMemo(() => {
    if (!activeStopId) return [];

    const rootId = rootIdOf(activeStopId);

    return locationsByRoot[rootId] ?? [];
  }, [activeStopId, locationsByRoot]);

  const handleHideStopLocations = React.useCallback(() => {
    if (!activeStopId) return;

    const rootId = rootIdOf(activeStopId);

    void toggleRoot(rootId, false);

    handleSetActiveStopId(rootId);

    const target =
      byId.get(rootId) ??
      allStops.find((s) => String(s.id) === rootId) ??
      selectedStopsForUi.find((s) => String(s.id) === rootId) ??
      allStops.find((s) => rootIdOf(String(s.id)) === rootId) ??
      selectedStopsForUi.find((s) => rootIdOf(String(s.id)) === rootId);

    if (!mapRef.current || !target) return;

    mapRef.current.panTo({
      lat: target.parentLat ?? target.lat,
      lng: target.parentLng ?? target.lng,
    });

    mapRef.current.setZoom(STOP_LOCATIONS_HIDE_BELOW_ZOOM - 1);
  }, [
    activeStopId,
    toggleRoot,
    handleSetActiveStopId,
    byId,
    allStops,
    selectedStopsForUi,
  ]);
  const handleShowStopLocations = React.useCallback(() => {
    if (!activeStopId) return;
    if (!mapRef.current) return;

    const rootId = rootIdOf(activeStopId);

    const locations =
      activeStopLocations.length > 0
        ? activeStopLocations
        : getLocationsFor(rootId, currentTpid);

    const fallbackTarget =
      activeStopLocation ??
      allStops.find((s) => rootIdOf(String(s.id)) === rootId) ??
      selectedStopsForUi.find((s) => rootIdOf(String(s.id)) === rootId);

    const stopsToFit =
      locations.length > 0 ? locations : fallbackTarget ? [fallbackTarget] : [];

    if (!stopsToFit.length) return;

    if (stopsToFit.length === 1) {
      const only = stopsToFit[0];

      mapRef.current.panTo({
        lat: only.lat,
        lng: only.lng,
      });

      mapRef.current.setZoom(STOP_LOCATIONS_HIDE_BELOW_ZOOM + 1);
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    for (const stop of stopsToFit) {
      bounds.extend({
        lat: stop.lat,
        lng: stop.lng,
      });
    }

    mapRef.current.fitBounds(bounds, {
      top: 80,
      right: 80,
      bottom: 80,
      left: 80,
    });

    window.setTimeout(() => {
      const currentZoom = mapRef.current?.getZoom();

      if (
        mapRef.current &&
        currentZoom != null &&
        currentZoom < STOP_LOCATIONS_HIDE_BELOW_ZOOM
      ) {
        mapRef.current.setZoom(STOP_LOCATIONS_HIDE_BELOW_ZOOM);
      }
    }, 0);
  }, [
    activeStopId,
    activeStopLocation,
    activeStopLocations,
    allStops,
    selectedStopsForUi,
    getLocationsFor,
    currentTpid,
  ]);

  return (
    <div className="relative flex h-[calc(100vh_-_90px)] md:h-[calc(100vh_-_100px)]">
      <>
        <div
          className={cn(
            "absolute right-3 top-3",
            "px-4 py-2",
            "gap-2",
            "flex flex-col items-end gap-2 md:flex-row md:items-center",
            "rounded border-white/60!",
            "bg-white/90!",
            "supports-[backdrop-filter:blur(1px)]:bg-white/60!",
            "backdrop-blur-md backdrop-saturate-100",
            "shadow-[0_8px_30px_rgba(0,0,0,0.10),0_0_0_1px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,0.90)]!",
            streetViewVisible ? "z-0" : "z-10"
          )}
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.04) 55%, rgba(255,255,255,0.12) 100%)",
          }}
        >
          {mapLoaded && <MapControllerButton map={mapRef.current} />}

          <ToolsPopover
            isDesktop={isDesktop}
            streetViewVisible={streetViewVisible}
            mobileSidebarOpen={mobileSidebarOpen}
            open={open}
            onOpenChange={setOpen}
            hasAnyFilter={hasAnyFilter}
            labelMode={labelMode}
            onLabelModeChange={setLabelMode}
            copied={copied}
            onCopyLink={handleCopyLink}
            onClearAll={handleClearAll}
          />
        </div>
        <div
          id="map-sidebar"
          className={cn(
            // Mobile:
            "fixed bottom-0 left-0 z-20",
            "flex h-[76px] w-full",
            "overflow-visible",

            // Desktop:
            "md:relative md:bottom-auto md:left-auto",
            "md:h-[calc(100vh_-_100px)] md:w-[112px]",
            "md:shrink-0",
            "md:bg-[var(--semantic-color-bg-primary)]",
            "md:border-r md:border-r-[var(--semantic-color-border-primary)]"
          )}
        >
          <TransitOverlay
            sidebarActivity={{
              stopAndLines:
                new Set([
                  ...selectedLines.map((line) => String(line.id)),
                  ...overviewLines.map((line) => String(line.id)),
                ]).size +
                new Set(selectedStopsForUi.map((stop) => stopKeyOf(stop.id)))
                  .size,

              zones: new Set(
                selectedZones.map((zone) => String(zone.id).split("_")[0])
              ).size,

              cityZones: selectedCityZones.length,

              extras:
                Number(parkingEnabled) +
                Number(ticketAgentsEnabled) +
                Number(ticketMachinesEnabled),

              measure: measurePoints.length,
            }}
            openSidebarId={openSidebarId as SidebarIdStrict | null}
            onSidebarToggle={handleSidebarToggle}
            selectedLines={selectedLines}
            activeLineId={activeLineIdStr}
            onAddAndActivateLine={handleAddAndActivateLine}
            onAddLinesToList={handleAddLineNetwork}
            onLineRemove={handleRemoveLine}
            selectedStops={selectedStopsForUi}
            activeStopId={activeStopId}
            onStopSelect={handleSelectStopFromSearch}
            onStopRemove={handleRemoveStop}
            onClearStops={handleClearStopsOnly}
            onClearActive={handleClearActive}
            setActiveStopId={handleSetActiveStopId}
            selectedCityZones={selectedCityZones}
            activeCityZoneId={activeCityZoneId}
            onCityZoneRemove={handleRemoveCityZone}
            onAddAndActivateCityZone={handleAddAndActivateCityZone}
            setActiveCityZoneId={handleSetActiveCityZoneId}
            ticketAgentsEnabled={ticketAgentsEnabled}
            onTicketAgentsEnabledChange={setTicketAgentsEnabled}
            setRequestedTicketAgentName={setRequestedTicketAgentName}
            ticketMachinesEnabled={ticketMachinesEnabled}
            onTicketMachinesEnabledChange={setTicketMachinesEnabled}
            setRequestedTicketMachineName={setRequestedTicketMachineName}
            unavailableLineIds={unavailableLineIds}
            liveStateByLine={lineStates}
            activeStopLocation={activeStopLocation}
            measuring={measuring}
            activeStopLocations={activeStopLocations}
            onToggleMeasuring={() => {
              setMeasuring((prev) => !prev);
            }}
            showStopLocationsButton={
              Boolean(activeStopId) && zoom < STOP_LOCATIONS_HIDE_BELOW_ZOOM
            }
            onShowStopLocations={handleShowStopLocations}
            onHideStopLocations={handleHideStopLocations}
            onClearMeasurement={clearMeasurement}
            totalDistanceKm={totalDistanceKm}
            measurePoints={measurePoints}
            allStops={allStops}
            onClearAll={handleClearAll}
            showLiveByLine={showLiveByLine}
            onToggleShowLiveForLine={onToggleShowLiveForLine}
            onAddLineById={addLineById}
            onAddStopFromLine={handleAddStopFromLine}
          />
        </div>

        <div
          className={cn(
            "w-full md:w-[100%] lg:w-[100%] transit-map-shell",
            measuring ? "measuring" : "",
            !isDesktop && "map-mobile-controls-offset"
          )}
        >
          <GoogleMap
            zoom={zoom}
            onIdle={handleIdle}
            onLoad={handleMapLoad}
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={center}
            options={mapOptions}
            onClick={handleMapClick}
          >
            <InitialFitBounds
              mapRef={mapRef}
              mapLoaded={mapLoaded}
              lineIds={lineIds}
              stopIds={stopIds}
              zoneIds={zoneIds}
              activeSelection={activeSelection}
              cityZoneIds={cityZoneIds}
              selectedLines={selectedLines}
              selectedStops={selectedStopsForUi}
              selectedZones={selectedZones}
              selectedCityZones={selectedCityZones}
              isDesktop={isDesktop}
              maxZoom={ZOOM_THRESHOLD_LABB}
              desktopLeftPadding={112 + 48}
              skipFit={hasSelectedLineNetwork && activeSelection == null}
            />

            {/* Lines  */}
            {selectedLines.map((line) => (
              <LinePolylines
                key={`selected-${line.id}`}
                line={line}
                activeLineId={activeLineIdStr}
                polyRefs={polyRefs}
                onClick={handleAddAndActivateLine}
              />
            ))}

            {overviewLines.map((line) => (
              <LinePolylines
                key={`overview-${line.id}`}
                line={line}
                activeLineId={activeLineIdStr}
                polyRefs={polyRefs}
                onClick={handleAddAndActivateLine}
              />
            ))}

            {/* Low zoom stop labels */}
            {lowZoomInfoStops.map((s) => (
              <LowZoomStopTooltip
                key={`tooltip-lo-${s.id}`}
                map={mapRef.current}
                stop={s}
              />
            ))}

            {/* Zones / Närtrafik */}
            {selectedZones.map((z) => (
              <ZonePolygon
                key={z.id}
                zone={z}
                active={z.id === activeZoneId}
                onClick={handleZoneClick}
              />
            ))}

            {/* InfoWindow for active zone */}
            {zoneInfo && (
              <ZoneInfoWindow
                zone={zoneInfo.zone}
                position={zoneInfo.position}
                stops={allStops}
                onStopSelect={(stop) => {
                  setZoneInfo(null);
                  void handleSelectStopFromSearch(stop);
                }}
                onClose={() => setZoneInfo(null)}
              />
            )}

            {cityZoneInfo && (
              <CityZoneInfoWindow
                zone={cityZoneInfo.zone}
                position={cityZoneInfo.position}
                onClose={() => setCityZoneInfo(null)}
              />
            )}

            {/* Live-traffic */}
            <LiveTrafficLayer
              overlaysByLine={liveOverlayByLine}
              lineIds={liveLineIds}
              lineLabelById={lineLabelById}
              markerScale={markerScale}
              markerZoomScale={liveMarkerZoomScale}
              markerScaleKey={`${markerScaleKey}-live:${liveMarkerZoomScaleKey}`}
            />

            {/* CityZones */}
            {selectedCityZones.map((z) => (
              <CityZonePolygon
                key={z.id}
                zone={z}
                active={z.id === activeCityZoneId}
                onClick={handleCityZoneClick}
              />
            ))}

            {/* measuring */}
            <MeasuringLayer measure={measure} />

            {/* POI: Parking / Agents / Machines */}
            <TransitPoisLayer
              mapRef={mapRef}
              mapLoaded={mapLoaded}
              zoomTo={ZOOM_THRESHOLD_LABB}
            />

            {ll && showMarker && (
              <>
                <Marker
                  position={ll}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 11,
                    fillColor: "#800020",
                    fillOpacity: 1,
                    strokeWeight: 3,
                    strokeColor: "#E6CCD2",
                  }}
                  onClick={() => setLlInfoOpen(true)}
                />

                {showPopup && llInfoOpen && (
                  <LlInfoWindow
                    position={ll}
                    title={llLabel}
                    onClose={() => setLlInfoOpen(false)}
                  />
                )}
              </>
            )}
            {displayedLocationRoots.size > 0 && (
              <StopLocationsLayer
                map={mapRef.current}
                locationsByRoot={viewportLocationsByRoot}
                expandedRoots={displayedLocationRoots}
                activeStopId={activeStopId}
                listedStopRootIds={listedStopRootIds}
                activeLineStopRootIds={activeLineStopRootIds}
                activeZoneColor={activeZoneColor}
                activeZoneStopRootIds={activeZoneStopRootIds}
                activeLineColor={activeLineColor}
                colors={stopColors}
                onClickStop={handleStopLocationClick}
              />
            )}

            {/* Clusters */}
            {mapRef.current && (
              <ClusterManager
                key={clusterKey}
                map={mapRef.current}
                stops={clusterStopsForMap}
                clusterRadius={scaledClusterRadius}
                activeStopId={activeStopId}
                focusedStopRootIds={focusedStopRootIds}
                dimmedOpacity={0.5}
                forceVisibleStopRootIds={forceVisibleStopRootIds}
                minIndividualBytesprio={
                  zoom < 10 ? 7 : zoom < 12 ? 5 : zoom < 14 ? 3 : 1
                }
                showClusterSingletonsFromZoom={14}
                iconRefreshKey={`${activeStopId ?? ""}-${
                  activeLineIdStr ?? ""
                }-${activeLineColor ?? ""}-${lineIds.join(
                  ","
                )}-${clusterFocusKey}-scale:${markerScaleKey}${lineStopScalePart}-listed:${stopIds.join(
                  ","
                )}-zone:${activeZoneMarkerKey}`}
                getStopIcon={
                  ((s, state) => getStopIconCb(s as Stop, state)) as (
                    s: {
                      id: string;
                      name: string;
                      lat: number;
                      lng: number;
                      type?: string;
                    },
                    state?: {
                      hovered?: boolean;
                      focused?: boolean;
                      focusedColor?: string;
                    }
                  ) => google.maps.Icon | string
                }
                getClusterIcon={getClusterIconCb}
                onStopClick={
                  ((s) => {
                    void handleSelectStopFromSearch(s as Stop);
                  }) as (s: {
                    id: string;
                    name: string;
                    lat: number;
                    lng: number;
                    type?: string;
                  }) => void
                }
                dimmed={
                  parkingEnabled || ticketAgentsEnabled || ticketMachinesEnabled
                }
                // clusterRadius={
                //   zoom < 10 ? 400 : zoom < 12 ? 340 : zoom < 14 ? 250 : 180
                //  }

                minClusterPoints={labelMode === "icon+name" ? 6 : 4}
                maxClusterZoom={INFOWINDOW_ZOOM_MIN}
                trackVisibleSingletons={shouldTrackSingletons}
                onVisibleSingletonsChange={setVisibleSingletonStopIds}
              />
            )}
          </GoogleMap>
        </div>
      </>
    </div>
  );
}
